import React from 'react';

/**
 * Page controls for the requisition lists.
 *
 * These tables were fetching everything and rendering it in one go, which is
 * what made the pages heavy. The server now returns a page at a time and
 * reports the full count in a header; this is the control that drives it.
 *
 * Renders nothing when there is only one page of the smallest size — controls
 * that can only be pressed to no effect are noise.
 */

const PAGE_SIZES = [10, 25, 50, 100];
const NAVY = '#0b318bff';

// A fixed five-wide window, clamped to the ends, with the first and last page
// always reachable. 22,826 pages cannot all be drawn, and the two a reader
// actually wants from a list that long are "the next one" and "the last one".
const WINDOW = 5;
const pageList = (current, pages) => {
  if (pages <= WINDOW + 2) {
    return Array.from({ length: pages }, (_, i) => i + 1);
  }
  let start = Math.max(1, current - Math.floor(WINDOW / 2));
  const end = Math.min(pages, start + WINDOW - 1);
  start = Math.max(1, end - WINDOW + 1);

  const out = [];
  if (start > 1) {
    out.push(1);
    // Only when a page is actually being hidden. "1 … 2" is a lie.
    if (start > 2) out.push('…');
  }
  for (let p = start; p <= end; p++) out.push(p);
  if (end < pages) {
    if (end < pages - 1) out.push('…');
    out.push(pages);
  }
  return out;
};

const arrowBtn = (enabled) => ({
  padding: '6px 14px',
  borderRadius: 8,
  border: `1.5px solid ${NAVY}`,
  background: NAVY,
  color: '#fff',
  fontSize: 12,
  fontWeight: 700,
  cursor: enabled ? 'pointer' : 'not-allowed',
  opacity: enabled ? 1 : 0.4,
  transition: 'all 0.15s',
});

const numBtn = (active) => ({
  minWidth: 32,
  padding: '6px 9px',
  borderRadius: 8,
  border: `1.5px solid ${active ? NAVY : '#cbd5e1'}`,
  background: active ? NAVY : '#fff',
  color: active ? '#fff' : '#0f172a',
  fontSize: 12,
  fontWeight: active ? 800 : 600,
  cursor: active ? 'default' : 'pointer',
  transition: 'all 0.15s',
});

export default function Pagination({ total = 0, skip = 0, limit = 10, onChange }) {
  const pages = Math.max(1, Math.ceil(total / limit));
  const current = Math.min(Math.floor(skip / limit) + 1, pages);
  const hasPrev = current > 1;
  const hasNext = current < pages;

  if (total <= PAGE_SIZES[0] && pages <= 1) return null;

  const go = (page) => onChange({ skip: (page - 1) * limit, limit });

  // Changing the size returns to page one. Staying on "page 4" while the rows
  // per page changes lands somewhere the reader did not ask to be.
  const resize = (next) => onChange({ skip: 0, limit: next });

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
      gap: 14, padding: '12px 16px', flexWrap: 'wrap',
    }}>
      <span style={{ fontSize: 11.5, color: '#64748b', fontWeight: 600 }}>
        Rows per page:
      </span>
      <select
        value={limit}
        onChange={(e) => resize(Number(e.target.value))}
        style={{
          padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 6,
          fontSize: 12, fontWeight: 700, color: '#0f172a', background: '#fff',
          cursor: 'pointer',
        }}
      >
        {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
      </select>

      <span style={{ fontSize: 11.5, color: '#64748b', fontWeight: 600 }}>
        {/* The row range, not just the page number: "showing 11–20 of 137" is
            what tells somebody whether the thing they want is behind them or
            ahead of them. */}
        {total === 0
          ? 'No records'
          : `${skip + 1}–${Math.min(skip + limit, total)} of ${total}`}
      </span>

      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" disabled={!hasPrev} style={arrowBtn(hasPrev)}
                onClick={() => hasPrev && go(current - 1)}>
          ‹ Previous
        </button>

        {pageList(current, pages).map((p, i) => (
          p === '…' ? (
            // Not a button: there is no single page it could sensibly go to.
            <span key={`gap-${i}`} style={{
              padding: '6px 4px', fontSize: 12, fontWeight: 700, color: '#94a3b8',
            }}>…</span>
          ) : (
            <button
              key={p}
              type="button"
              style={numBtn(p === current)}
              onClick={() => p !== current && go(p)}
              aria-current={p === current ? 'page' : undefined}
            >
              {p}
            </button>
          )
        ))}

        <button type="button" disabled={!hasNext} style={arrowBtn(hasNext)}
                onClick={() => hasNext && go(current + 1)}>
          Next ›
        </button>
      </div>
    </div>
  );
}
